import { useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useIsArtisan } from "@/hooks/useArtisanProfile";
import { supabase } from "@/integrations/supabase/client";
import { useLocalizedNavigate } from "@/hooks/useLocalizedNavigate";

const GOOGLE_ARTISAN_KEY = "hirafi_google_artisan_type";

/**
 * Page de callback après confirmation d'email ou OAuth Supabase.
 * Redirige vers /dashboard (artisan) ou /mes-annonces (client).
 *
 * Cas spécial : inscription artisan via Google OAuth.
 * Avant la redirection OAuth, Inscription.tsx stocke dans localStorage
 * `hirafi_google_artisan_type` ('bricoleur' | 'entreprise').
 * Ici, on appelle la RPC setup_artisan_from_oauth pour finaliser le profil.
 */
const AuthCallback = () => {
  const { user, loading: authLoading } = useAuth();
  const { data: isArtisan, isLoading: artisanLoading } = useIsArtisan();
  const navigate = useLocalizedNavigate();
  const setupDone = useRef(false);

  useEffect(() => {
    if (authLoading || artisanLoading) return;

    if (!user) {
      navigate("/connexion", { replace: true });
      return;
    }

    const pendingArtisanType = localStorage.getItem(GOOGLE_ARTISAN_KEY);

    if (pendingArtisanType && !isArtisan && !setupDone.current) {
      setupDone.current = true;
      localStorage.removeItem(GOOGLE_ARTISAN_KEY);

      supabase
        .rpc("setup_artisan_from_oauth" as any, { p_artisan_type: pendingArtisanType })
        .then(() => {
          navigate("/dashboard", { replace: true });
        })
        .catch(() => {
          navigate("/dashboard", { replace: true });
        });
      return;
    }

    navigate(isArtisan ? "/dashboard" : "/mes-annonces", { replace: true });
  }, [user, authLoading, isArtisan, artisanLoading, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <p className="text-sm text-muted-foreground">Connexion en cours…</p>
    </div>
  );
};

export default AuthCallback;
